import React, { useEffect } from 'react';
import {
  Modal,
  Typography,
  Form,
  Input,
  Select,
  Switch,
  Button,
  useToast,
} from '@crisellevaldez/speculo-ui';
import { BellRing, Home, LayoutGrid, MapPin, Info } from 'lucide-react';
import { IconPill } from '../../common/IconPill';
import { useForm, Controller, Control } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { eAlertSchema, EAlertFormData } from '../../../schemas/eAlert.schema';
import { Contact } from '../../../types/contact.types';
import {
  useRegisterEAlert,
  useTestEAlert,
  useUpdateEAlertFilters,
} from '../../../hooks/contacts/useEAlerts';
import { useUserProfile } from '../../../hooks/contacts/useUserProfile';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { US_STATES } from '../../../constants/states';
import { CreatableSelect } from '../../common/CreatableSelect';
import { useUSCanadaCities } from '../../../hooks/cities/useUSCanadaCities';
import { useUser } from '../../../hooks/useUser';
import { AdminLockOverlay } from '../client/AdminLockOverlay';

// Section header component for consistent styling
const _SectionHeader: React.FC<{ icon: React.ReactNode; title: string }> = ({ icon, title }) => (
  <div className="text-base font-semibold text-gray-800 mb-5 flex items-center">
    <span className="mr-2 text-primary">{icon}</span>
    {title}
  </div>
);

// Feature toggle component for consistent styling
const FeatureToggle: React.FC<{
  label: string;
  name: keyof EAlertFormData;
  control: Control<EAlertFormData>;
}> = ({ label, name, control }) => (
  <div className="flex items-center justify-between">
    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Switch
          checked={!!field.value}
          onChange={(checked) => field.onChange(checked)}
          size="sm"
        />
      )}
    />
  </div>
);

interface EAlertModalProps {
  isOpen: boolean;
  onClose: () => void;
  contact: Contact | null | { id: string };
  isEditMode?: boolean;
}

// Defaults are aligned with backend `eAlertSystem/config.py` source-of-truth
// (DEFAULT_INTERVAL='daily', DEFAULT_PROPERTY_TYPE='Single Family Residence',
// DEFAULT_MIN_PRICE=200000). See SPEC-1287 audit.
const getDefaultFormValues = (contactId: string): EAlertFormData => ({
  contact_id: contactId,
  interval: 'daily',
  city: '',
  state: '',
  subdivision: '',
  q: '',
  bedrooms: undefined,
  bathrooms: undefined,
  min_price: undefined,
  max_price: undefined,
  property_type: 'Single Family Residence',
  propertysubtype: '',
  status: 'Active',
  waterfront: false,
  pool: false,
  basement: false,
  garage: false,
  city_view: false,
  mountain_view: false,
  park_view: false,
  water_view: false,
  sqft_min: undefined,
  sqft_max: undefined,
});

export const EAlertModal: React.FC<EAlertModalProps> = ({
  isOpen,
  onClose,
  contact,
  isEditMode = false,
}) => {
  const { showToast } = useToast();
  const { organization } = useOrganizationStore();
  const contactId = contact && 'id' in contact ? contact.id : '';

  const { data: userProfile } = useUserProfile({
    contactId,
    organizationId: organization?.id || '',
    enabled: isOpen && isEditMode && !!contactId,
  });

  // Use the custom hooks for eAlert operations
  const { mutate: registerEAlert, isPending: isSubmitting } = useRegisterEAlert();
  const { isAdmin } = useUser();
  const { mutate: updateEAlertFilters, isPending: isUpdating } = useUpdateEAlertFilters();
  const { mutate: testEAlert, isPending: isTestSubmitting } = useTestEAlert();

  // US states + Canadian provinces, served from a local constant so we
  // don't fetch the full Cities collection just to populate the dropdown.
  const stateOptions = US_STATES;

  // Initialize form with react-hook-form and zod validation
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<EAlertFormData>({
    resolver: zodResolver(eAlertSchema),
    defaultValues: getDefaultFormValues(contactId),
  });

  // Cities are derived from the currently-selected state (US or Canada).
  // The country-state-city package is dynamic-imported inside the hook so
  // the data doesn't sit in the initial bundle.
  const selectedState = watch('state');
  const { cities: cityOptions, isLoading: isCitiesLoading } =
    useUSCanadaCities(selectedState);


  useEffect(() => {
    if (!isOpen) return;

    if (!isEditMode) {
      reset(getDefaultFormValues(contactId));
      return;
    }

    if (!contactId) return;

    // Edit mode without a loaded profile (still fetching, or fetch errored):
    // still seed the form with the right contact_id so the user can submit.
    // Without this the form's contact_id stays empty and zod blocks the
    // submit silently, making "Update Filters" look broken.
    if (!userProfile) {
      reset(getDefaultFormValues(contactId));
      return;
    }

    // The /ealert/profile endpoint returns two shapes depending on which
    // backend path served the org:
    //   * legacy Firestore — top-level `city`, `state`, `schedule`, etc.
    //   * Spanner profile (post-#2428) — same fields live inside the
    //     nested `preferences` object, with city/state as arrays.
    // Read the top-level first (so legacy orgs still pre-fill), then
    // fall back to `preferences[key]`, unwrapping arrays. Without this,
    // Spanner-backed contacts opened "Update eAlert" with every field
    // blank because we only looked at the top level.
    const prefs =
      (userProfile.preferences as Record<string, unknown> | undefined) ?? {};
    const fromPrefs = (key: string): unknown => {
      const v = prefs[key];
      if (Array.isArray(v)) return v[0];
      return v;
    };
    const pickString = (top: unknown, key: string): string => {
      if (typeof top === 'string' && top.length > 0) return top;
      const v = fromPrefs(key);
      return typeof v === 'string' ? v : '';
    };
    const pickNumber = (top: unknown, key: string): number | undefined => {
      if (top != null && top !== '') {
        const n = Number(top);
        if (!Number.isNaN(n)) return n;
      }
      const v = fromPrefs(key);
      if (v == null || v === '') return undefined;
      const n = Number(v);
      return Number.isNaN(n) ? undefined : n;
    };
    const pickBool = (top: unknown, key: string): boolean => {
      if (top === true) return true;
      const v = fromPrefs(key);
      return v === true;
    };

    const intervalCandidate =
      (userProfile.schedule as string | undefined) ??
      (userProfile.ealerts_interval as string | undefined) ??
      (fromPrefs('interval') as string | undefined);
    const normalizedInterval =
      intervalCandidate === 'daily' ||
      intervalCandidate === 'weekly' ||
      intervalCandidate === 'bi-weekly'
        ? intervalCandidate
        : 'daily';

    reset({
      ...getDefaultFormValues(contactId),
      contact_id: contactId,
      interval: normalizedInterval,
      city: pickString(userProfile.city, 'city'),
      state: pickString(userProfile.state, 'state'),
      subdivision: pickString(userProfile.subdivision, 'subdivision'),
      q: pickString(userProfile.q, 'q'),
      bedrooms: pickNumber(userProfile.bedrooms, 'bedrooms'),
      bathrooms: pickNumber(userProfile.bathrooms, 'bathrooms'),
      min_price: pickNumber(userProfile.min_price, 'min_price'),
      max_price: pickNumber(userProfile.max_price, 'max_price'),
      property_type:
        pickString(userProfile.property_type, 'property_type') ||
        'Single Family Residence',
      propertysubtype: pickString(userProfile.propertysubtype, 'propertysubtype'),
      status: (() => {
        // Top-level `status` on this response is the eAlert registration
        // status ("active" / "inactive") — NOT the listing status the
        // form wants. Only accept it when it looks like a listing status
        // string, otherwise pull from preferences (where Active/Sold/etc.
        // lives) and finally fall back to "Active".
        const top = userProfile.status;
        const topVal = Array.isArray(top) ? top[0] : top;
        const ealertStatusTokens = new Set(['active', 'inactive', 'paused', 'pending']);
        const looksLikeRegistration =
          typeof topVal === 'string' && ealertStatusTokens.has(topVal.toLowerCase());
        if (typeof topVal === 'string' && !looksLikeRegistration) return topVal;
        const prefStatus = fromPrefs('status');
        if (typeof prefStatus === 'string') return prefStatus;
        if (Array.isArray(prefStatus) && typeof prefStatus[0] === 'string') {
          return prefStatus[0];
        }
        return 'Active';
      })(),
      waterfront: pickBool(userProfile.waterfront, 'waterfront'),
      pool: pickBool(userProfile.pool, 'pool'),
      basement: pickBool(userProfile.basement, 'basement'),
      garage: pickBool(userProfile.garage, 'garage'),
      city_view: pickBool(userProfile.city_view, 'city_view'),
      mountain_view: pickBool(userProfile.mountain_view, 'mountain_view'),
      park_view: pickBool(userProfile.park_view, 'park_view'),
      water_view: pickBool(userProfile.water_view, 'water_view'),
      sqft_min: pickNumber(userProfile.sqft_min, 'sqft_min'),
      sqft_max: pickNumber(userProfile.sqft_max, 'sqft_max'),
    });
  }, [contactId, isEditMode, isOpen, reset, userProfile]);

  // Filter out false boolean values from form data
  const filterFalseValues = (data: EAlertFormData): EAlertFormData => {
    const filteredData = { ...data };

    // List of boolean fields that should be omitted if false
    const booleanFields = [
      'waterfront',
      'pool',
      'basement',
      'garage',
      'city_view',
      'mountain_view',
      'park_view',
      'water_view',
    ] as const;

    // Remove fields with false values
    booleanFields.forEach((field) => {
      if (filteredData[field] === false) {
        delete filteredData[field];
      }
    });

    return filteredData as EAlertFormData;
  };

  // Handle form submission for registration or update
  const onSubmit = (data: EAlertFormData) => {
    // Filter out false boolean values before submitting
    const filteredData = filterFalseValues(data);
    filteredData.organization_id = organization?.id || '';

    // Align min_price fallback with BE DEFAULT_MIN_PRICE (200000) from
    // eAlertSystem/config.py. The previous 50000 matched BE's
    // SAFE_FALLBACK_MIN_PRICE (last-resort), not the standard default.
    if (!filteredData.min_price) {
      filteredData.min_price = 200000;
    }

    if (isEditMode) {
      updateEAlertFilters(filteredData, {
        onSuccess: () => {
          showToast({
            message: 'eAlert filters updated successfully',
            variant: 'success',
          });
          reset();
          onClose();
        },
        onError: (error) => {
          const message =
            error instanceof Error && error.message
              ? error.message
              : 'Failed to update eAlert filters';
          showToast({ message, variant: 'error' });
        },
      });
    } else {
      // Register new eAlert
      registerEAlert(filteredData, {
        onSuccess: () => {
          showToast({
            message: 'eAlert registered successfully',
            variant: 'success',
          });
          reset();
          onClose();
        },
        onError: (error) => {
          const message =
            error instanceof Error && error.message
              ? error.message
              : 'Failed to register eAlert';
          showToast({ message, variant: 'error' });
        },
      });
    }
  };

  // Handle test submission
  const _onTestSubmit = handleSubmit((data) => {
    // Filter out false boolean values before submitting
    const filteredData = filterFalseValues(data);
    filteredData.organization_id = organization?.id || '';

    // Align min_price fallback with BE DEFAULT_MIN_PRICE (200000) from
    // eAlertSystem/config.py. The previous 50000 matched BE's
    // SAFE_FALLBACK_MIN_PRICE (last-resort), not the standard default.
    if (!filteredData.min_price) {
      filteredData.min_price = 200000;
    }

    testEAlert(filteredData, {
      onSuccess: (responseData) => {
        console.log('Test eAlert Response:', responseData);
        showToast({
          message: 'eAlert test completed successfully',
          variant: 'success',
        });
      },
      onError: (error) => {
        const message =
          error instanceof Error && error.message ? error.message : 'eAlert test failed';
        showToast({ message, variant: 'error' });
      },
    });
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <Modal.Header>
        <div className="flex items-center justify-between w-full">
          <Typography variant="h3">
            {isEditMode ? 'Update Property eAlert Filters' : 'Set Up Property eAlert'}
          </Typography>
        </div>
      </Modal.Header>
      <Modal.Body className="py-8">
        <AdminLockOverlay locked={!isAdmin} subject="eAlerts">
        <div className="space-y-6">
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700/50 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Info className="h-4 w-4 text-yellow-800 dark:text-yellow-300" />
              <p className="text-sm text-yellow-800 dark:text-yellow-300">
                Set up automated property alerts for this contact. They will receive notifications
                based on their preferences.
              </p>
            </div>
          </div>
          <form id="ealert-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-6 border border-gray-300 dark:border-white/10">
              <div className="flex items-center gap-4 mb-3">
                <IconPill icon={BellRing} size="lg" />
                <h4 className="text-sm font-bold">Alert Information</h4>
              </div>
              <hr className="border-gray-200 dark:border-white/10" />
              <div className="mt-5 space-y-4">
                <Form.Control className="mt-4">
                  <Form.Label required>Alert Frequency</Form.Label>
                  <Controller
                    name="interval"
                    control={control}
                    render={({ field }) => (
                      <Select
                        placeholder="Select frequency"
                        options={[
                          { value: 'daily', label: 'Daily' },
                          { value: 'weekly', label: 'Weekly' },
                          { value: 'bi-weekly', label: 'Bi-Weekly' },
                        ]}
                        {...field}
                        error={errors.interval?.message}
                      />
                    )}
                  />
                </Form.Control>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-6 border border-gray-300 dark:border-white/10">
              <div className="flex items-center gap-4 mb-3">
                <IconPill icon={MapPin} size="lg" />
                <h4 className="text-sm font-bold">Location</h4>
              </div>
              <hr className="border-gray-200 dark:border-white/10" />
              <div className="mt-5 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <Form.Control>
                    <Form.Label>State</Form.Label>
                    <Controller
                      name="state"
                      control={control}
                      render={({ field }) => (
                        <Select
                          placeholder="Select state"
                          options={stateOptions}
                          searchable={true}
                          {...field}
                          onChange={(val) => {
                            field.onChange(val);
                            setValue('city', '');
                          }}
                          error={errors.state?.message}
                        />
                      )}
                    />
                  </Form.Control>
                  <Form.Control>
                    <Form.Label>City</Form.Label>
                    <Controller
                      name="city"
                      control={control}
                      render={({ field }) => (
                        <CreatableSelect
                          value={field.value ?? ''}
                          onChange={field.onChange}
                          options={cityOptions}
                          placeholder={
                            selectedState
                              ? isCitiesLoading
                                ? 'Loading cities…'
                                : 'Type or pick a city'
                              : 'Select a state first'
                          }
                          isLoading={isCitiesLoading}
                          disabled={!selectedState}
                          error={errors.city?.message}
                        />
                      )}
                    />
                  </Form.Control>
                  <Form.Control>
                    <Form.Label>Subdivision</Form.Label>
                    <Controller
                      name="subdivision"
                      control={control}
                      render={({ field }) => (
                        <Input
                          placeholder="Enter subdivision"
                          {...field}
                          error={errors.subdivision?.message}
                        />
                      )}
                    />
                  </Form.Control>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-6 border border-gray-300 dark:border-white/10">
              <div className="flex items-center gap-4 mb-3">
                <IconPill icon={Home} size="lg" />
                <h4 className="text-sm font-bold">Search Parameters</h4>
              </div>
              <hr className="border-gray-200 dark:border-white/10" />
              <div className="mt-5 space-y-4">
                <Form.Control className="mb-4">
                  <Form.Label>Search Query</Form.Label>
                  <Controller
                    name="q"
                    control={control}
                    render={({ field }) => (
                      <Input
                        placeholder="e.g., modern homes, waterfront property"
                        {...field}
                        error={errors.q?.message}
                      />
                    )}
                  />
                </Form.Control>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-6 border border-gray-300 dark:border-white/10">
              <div className="flex items-center gap-4 mb-3">
                <IconPill icon={Home} size="lg" />
                <h4 className="text-sm font-bold">Property Details</h4>
              </div>
              <hr className="border-gray-200 dark:border-white/10" />
              <div className="mt-5 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <Form.Control>
                    <Form.Label>Bedrooms</Form.Label>
                    <Controller
                      name="bedrooms"
                      control={control}
                      render={({ field: { onChange, value, ...rest } }) => (
                        <Select
                          placeholder="Select bedrooms"
                          options={[
                            { value: '', label: 'Any' },
                            { value: '1', label: '1 Bedroom' },
                            { value: '2', label: '2 Bedrooms' },
                            { value: '3', label: '3 Bedrooms' },
                            { value: '4', label: '4 Bedrooms' },
                            { value: '5', label: '5 Bedrooms' },
                          ]}
                          value={value?.toString() || ''}
                          onChange={(val: string | string[]) => {
                            const singleVal = Array.isArray(val) ? val[0] : val;
                            onChange(singleVal === '' ? undefined : parseInt(singleVal, 10));
                          }}
                          {...rest}
                        />
                      )}
                    />
                  </Form.Control>
                  <Form.Control>
                    <Form.Label>Bathrooms</Form.Label>
                    <Controller
                      name="bathrooms"
                      control={control}
                      render={({ field: { onChange, value, ...rest } }) => (
                        <Select
                          placeholder="Select bathrooms"
                          options={[
                            { value: '', label: 'Any' },
                            { value: '1', label: '1 Bathroom' },
                            { value: '2', label: '2 Bathrooms' },
                            { value: '3', label: '3 Bathrooms' },
                            { value: '4', label: '4 Bathrooms' },
                            { value: '5', label: '5 Bathrooms' },
                          ]}
                          value={value?.toString() || ''}
                          onChange={(val: string | string[]) => {
                            const singleVal = Array.isArray(val) ? val[0] : val;
                            onChange(singleVal === '' ? undefined : parseFloat(singleVal));
                          }}
                          {...rest}
                        />
                      )}
                    />
                  </Form.Control>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <Form.Control>
                    <Form.Label>Minimum Price</Form.Label>
                    <Controller
                      name="min_price"
                      control={control}
                      render={({ field }) => (
                        <Input
                          type="text"
                          inputMode="numeric"
                          placeholder="Enter minimum price"
                          {...field}
                          error={errors.min_price?.message}
                        />
                      )}
                    />
                  </Form.Control>
                  <Form.Control>
                    <Form.Label>Maximum Price</Form.Label>
                    <Controller
                      name="max_price"
                      control={control}
                      render={({ field }) => (
                        <Input
                          type="text"
                          inputMode="numeric"
                          placeholder="Enter maximum price"
                          {...field}
                          error={errors.max_price?.message}
                        />
                      )}
                    />
                  </Form.Control>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <Form.Control>
                    <Form.Label>Property Type</Form.Label>
                    <Controller
                      name="property_type"
                      control={control}
                      render={({ field }) => (
                        <Select
                          placeholder="Select property type"
                          options={[
                            { value: '', label: 'Any' },
                            { value: 'Single Family Residence', label: 'Single Family Residence' },
                            { value: 'Condominium', label: 'Condominium' },
                            { value: 'Townhouse', label: 'Townhouse' },
                            { value: 'Land', label: 'Land' },
                          ]}
                          {...field}
                        />
                      )}
                    />
                  </Form.Control>
                  <Form.Control>
                    <Form.Label>Status</Form.Label>
                    <Controller
                      name="status"
                      control={control}
                      render={({ field }) => (
                        <Select
                          placeholder="Select status"
                          options={[
                            { value: '', label: 'Any' },
                            { value: 'Active', label: 'Active' },
                            { value: 'Pending', label: 'Pending' },
                            { value: 'Closed', label: 'Closed' },
                          ]}
                          {...field}
                        />
                      )}
                    />
                  </Form.Control>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <Form.Control>
                    <Form.Label>Minimum Square Footage</Form.Label>
                    <Controller
                      name="sqft_min"
                      control={control}
                      render={({ field }) => (
                        <Input
                          type="text"
                          inputMode="numeric"
                          placeholder="Enter minimum sqft"
                          {...field}
                          error={errors.sqft_min?.message}
                        />
                      )}
                    />
                  </Form.Control>
                  <Form.Control>
                    <Form.Label>Maximum Square Footage</Form.Label>
                    <Controller
                      name="sqft_max"
                      control={control}
                      render={({ field }) => (
                        <Input
                          type="text"
                          inputMode="numeric"
                          placeholder="Enter maximum sqft"
                          {...field}
                          error={errors.sqft_max?.message}
                        />
                      )}
                    />
                  </Form.Control>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-6 border border-gray-300 dark:border-white/10">
              <div className="flex items-center gap-4 mb-3">
                <IconPill icon={LayoutGrid} size="lg" />
                <h4 className="text-sm font-bold">Features</h4>
              </div>
              <hr className="border-gray-200 dark:border-white/10" />
              <div className="mt-5 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <FeatureToggle label="Waterfront" name="waterfront" control={control} />
                  <FeatureToggle label="Pool" name="pool" control={control} />
                  <FeatureToggle label="Basement" name="basement" control={control} />
                  <FeatureToggle label="Garage" name="garage" control={control} />
                  <FeatureToggle label="City View" name="city_view" control={control} />
                  <FeatureToggle label="Mountain View" name="mountain_view" control={control} />
                  <FeatureToggle label="Park View" name="park_view" control={control} />
                  <FeatureToggle label="Water View" name="water_view" control={control} />
                </div>
              </div>
            </div>
          </form>
        </div>
        </AdminLockOverlay>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex flex-col md:flex-row items-start md:items-center gap-4 w-full">
          <div className="flex gap-2 w-full md:w-auto ml-auto justify-end">
            {/*
            <Button
              variant="outline"
              onClick={onTestSubmit}
              isLoading={isTestSubmitting}
              disabled={isSubmitting}
            >
              Test eAlert
            </Button> */}
            <Button
              variant="primary"
              type="submit"
              form="ealert-form"
              isLoading={isSubmitting || isUpdating}
              disabled={isTestSubmitting || !isAdmin}
            >
              {isEditMode ? 'Update Filters' : 'Register eAlert'}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
